import { format } from 'date-fns';

import {
    AddressType,
    CountryCode,
    DataType,
    EmploymentStatus,
    GlobalAccountRoleType,
    MembershipType,
    OnboardingDocumentReadState,
    OnboardingDocumentType,
    OnboardingFileProcessingStatus,
    OnboardingFileView,
    OnboardingStage,
    PersonOrg,
    PersonOrgAddress,
    PersonOrgAddressCreateRequest,
    PersonOrgLink,
    PersonOrgLinkSaveRequest,
    PersonOrgOnboardingProfileSaveRequest,
    PersonOrgOnboardingUpdateRequest,
    PersonOrgProfile,
    PersonOrgRelation,
    PersonOrgRelationType,
    PersonOrgSubtype,
    PersonOrgType,
    TrustedContact,
    UnencryptedData,
    WealthSource,
} from '@ndvr/customer-api';

import { encryptData, getPersonOrgName } from 'utils';

export const STAGES: { value: OnboardingStage; label: string }[] = [
    { value: OnboardingStage.NOT_INVITED, label: 'Not Invited' },
    { value: OnboardingStage.INVITED, label: 'Invited' },
    { value: OnboardingStage.CLIENT_IN_PROGRESS, label: 'Client in Progress' },
    { value: OnboardingStage.CLARO_REVIEW, label: 'Claro Review' },
    { value: OnboardingStage.APPLICATION_OUTSTANDING, label: 'Application Outstanding' },
    { value: OnboardingStage.APPLICATION_SIGNED, label: 'Application Signed' },
    { value: OnboardingStage.ACCOUNTS_OPENED, label: 'Accounts Opened' },
    { value: OnboardingStage.ASSETS_RECEIVED, label: 'Assets Received' },
];

/**
 * The fee schedule is picked while the client fills in the onboarding or Claro reviews it; the billing group is created
 * from it when the accounts are opened.
 */
export const feeScheduleEditable = (stage: OnboardingStage | undefined) =>
    stage === OnboardingStage.CLIENT_IN_PROGRESS || stage === OnboardingStage.CLARO_REVIEW;

export const DOCUMENT_TYPE_LABELS: Record<OnboardingDocumentType, string> = {
    [OnboardingDocumentType.BROKERAGE_STATEMENT]: 'Account statement',
    [OnboardingDocumentType.BANK_STATEMENT]: 'Bank statement',
    [OnboardingDocumentType.VOIDED_CHECK]: 'Voided check',
    [OnboardingDocumentType.PHOTO_ID]: 'Photo ID',
    [OnboardingDocumentType.TRUST_DOCUMENT]: 'Trust document',
    [OnboardingDocumentType.TAX_RETURN]: 'Tax return',
    [OnboardingDocumentType.OTHER]: 'Other',
    [OnboardingDocumentType.UNREADABLE]: 'Unreadable',
};

/** Claro reads the documents while the client fills in the onboarding or while Claro reviews it. */
export const documentsReadable = (stage: OnboardingStage | undefined) =>
    stage === OnboardingStage.CLIENT_IN_PROGRESS || stage === OnboardingStage.CLARO_REVIEW;

/** The documents are being read, or the household is being built from them; another reading cannot start. */
export const isReadingDocuments = (state: OnboardingDocumentReadState | undefined) =>
    state === OnboardingDocumentReadState.READING || state === OnboardingDocumentReadState.BUILDING;

/** A file nothing usable was read from yet: not read yet, failed, or found unreadable. */
export const needsReading = (file: OnboardingFileView) =>
    file.processingStatus === OnboardingFileProcessingStatus.UPLOADED ||
    file.processingStatus === OnboardingFileProcessingStatus.FAILED ||
    file.documentType === OnboardingDocumentType.UNREADABLE;

/** How far the file is read, or what reading found in it and any issue with it. */
export const fileReadLabel = (file: OnboardingFileView) => {
    switch (file.processingStatus) {
        case OnboardingFileProcessingStatus.UPLOADED:
            return 'Not read yet';
        case OnboardingFileProcessingStatus.PENDING:
            return 'Waiting to be read';
        case OnboardingFileProcessingStatus.PROCESSING:
            return 'Reading…';
        case OnboardingFileProcessingStatus.FAILED:
            return 'Reading failed';
    }
    if (!file.documentType) return 'Read';
    const type = DOCUMENT_TYPE_LABELS[file.documentType];
    // An unreadable file's issue only repeats its type
    return file.processingIssue && file.documentType !== OnboardingDocumentType.UNREADABLE
        ? `${type} · ${file.processingIssue.toLowerCase()}`
        : type;
};

/** `UnencryptedData` or a plain field as display text; empty when unset. */
export const text = (value?: UnencryptedData | string) => (typeof value === 'string' ? value : value?.plainText) ?? '';

export const personName = (personOrg?: PersonOrg) =>
    personOrg
        ? personOrg.personOrgType === PersonOrgType.PERSON
            ? [text(personOrg.firstName), text(personOrg.middleName), text(personOrg.lastName)]
                  .filter(Boolean)
                  .join(' ') || 'Unnamed person'
            : getPersonOrgName({ ...personOrg, orgName: text(personOrg.orgName) || undefined })
        : '';

// ---------------------------------------------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------------------------------------------

export enum PersonKind {
    CLIENT = 'CLIENT',
    ENTITY = 'ENTITY',
    FAMILY = 'FAMILY',
    PROFESSIONAL = 'PROFESSIONAL',
}

/**
 * The household role label that marks a person's kind. Trusted professionals are secondary users whose label is their
 * role; clients and family added here carry their kind, and unlabelled people are told apart by what they own.
 */
export const CLIENT_LABEL = 'Client';
export const FAMILY_LABEL = 'Family';
export const PROFESSIONAL_ROLES = ['Accountant', 'Lawyer'] as const;
/** The role choice that takes a typed role instead. */
export const CUSTOM_ROLE = 'Custom';

/** A professional's role as the form edits it: a listed role, or `CUSTOM_ROLE` and the typed one. */
export interface RoleChoice {
    role: string;
    customRole: string;
}

export const toRoleChoice = (roleLabel = ''): RoleChoice =>
    !roleLabel || PROFESSIONAL_ROLES.some((role) => role === roleLabel)
        ? { role: roleLabel, customRole: '' }
        : { role: CUSTOM_ROLE, customRole: roleLabel };

/** The household role label to save; empty until a role is picked (and typed, for a custom one). */
export const roleLabelOf = ({ role, customRole }: RoleChoice) => (role === CUSTOM_ROLE ? customRole.trim() : role);

export const ENTITY_TYPES: { value: string; label: string; type: PersonOrgType; subtype: PersonOrgSubtype }[] = [
    {
        value: PersonOrgSubtype.REVOCABLE_TRUST,
        label: 'Revocable trust',
        type: PersonOrgType.TRUST,
        subtype: PersonOrgSubtype.REVOCABLE_TRUST,
    },
    {
        value: PersonOrgSubtype.IRREVOCABLE_TRUST,
        label: 'Irrevocable trust',
        type: PersonOrgType.TRUST,
        subtype: PersonOrgSubtype.IRREVOCABLE_TRUST,
    },
    {
        value: PersonOrgSubtype.GRANTOR_RETAINED_ANNUITY_TRUST,
        label: 'GRAT',
        type: PersonOrgType.TRUST,
        subtype: PersonOrgSubtype.GRANTOR_RETAINED_ANNUITY_TRUST,
    },
    {
        value: PersonOrgSubtype.LIMITED_LIABILITY_COMPANY,
        label: 'LLC',
        type: PersonOrgType.ORGANIZATION,
        subtype: PersonOrgSubtype.LIMITED_LIABILITY_COMPANY,
    },
    {
        value: PersonOrgSubtype.INCORPORATED_COMPANY,
        label: 'Corporation',
        type: PersonOrgType.ORGANIZATION,
        subtype: PersonOrgSubtype.INCORPORATED_COMPANY,
    },
    {
        value: PersonOrgSubtype.PARTNERSHIP,
        label: 'Partnership',
        type: PersonOrgType.ORGANIZATION,
        subtype: PersonOrgSubtype.PARTNERSHIP,
    },
];

/**
 * A role in an entity, as one or two links of the entity's own type (the backend refuses a trust role on a business).
 */
export const ENTITY_ROLES: { value: string; label: string; parent: PersonOrgType; types: MembershipType[] }[] = [
    { value: 'TRUSTEE', label: 'Trustee', parent: PersonOrgType.TRUST, types: [MembershipType.TRUST_TRUSTEE] },
    { value: 'GRANTOR', label: 'Grantor', parent: PersonOrgType.TRUST, types: [MembershipType.TRUST_GRANTOR] },
    {
        value: 'TRUSTEE_AND_GRANTOR',
        label: 'Trustee and Grantor',
        parent: PersonOrgType.TRUST,
        types: [MembershipType.TRUST_TRUSTEE, MembershipType.TRUST_GRANTOR],
    },
    {
        value: 'BENEFICIAL_OWNER',
        label: 'Beneficial owner',
        parent: PersonOrgType.ORGANIZATION,
        types: [MembershipType.BENEFICIAL_OWNER],
    },
    {
        value: 'MANAGING_OWNER',
        label: 'Managing owner',
        parent: PersonOrgType.ORGANIZATION,
        types: [MembershipType.MANAGING_OWNER],
    },
];

/** The roles an entity of this type takes, the first being the default of a new row. */
export const entityRolesFor = (entityType: PersonOrgType) => ENTITY_ROLES.filter((role) => role.parent === entityType);

const ENTITY_ROLE_TYPES = new Set(ENTITY_ROLES.flatMap((role) => role.types));

/** The types whose links carry a membership percent, as `MembershipType.hasMembershipPercent` on the backend. */
const PERCENT_TYPES = new Set([
    MembershipType.PRESIDENT,
    MembershipType.DIRECTOR,
    MembershipType.MANAGING_OWNER,
    MembershipType.BENEFICIAL_OWNER,
    MembershipType.TRUST_GRANTOR,
    MembershipType.TRUST_BENEFICIARY,
    MembershipType.TRUST_CONTINGENT_BENEFICIARY,
]);

/** What the percent totals of each editable type are called. */
export const PERCENT_ROLE_LABELS: Partial<Record<MembershipType, string>> = {
    [MembershipType.TRUST_GRANTOR]: 'Grantors',
    [MembershipType.BENEFICIAL_OWNER]: 'Beneficial owners',
    [MembershipType.MANAGING_OWNER]: 'Managing owners',
};

export const membershipTypesOf = (role: string) => ENTITY_ROLES.find((r) => r.value === role)?.types ?? [];

/** The one type of a role that carries a percent (a trustee and grantor's is the grantor's), if any. */
export const percentTypeOf = (role: string) => membershipTypesOf(role).find((type) => PERCENT_TYPES.has(type));

export interface RelatedPerson {
    personOrgGUID: string;
    /** An `ENTITY_ROLES` value. */
    role: string;
    /** The membership percent as typed, for roles that carry one; empty otherwise. */
    percent: string;
}

/** An entity's people and their roles, a trustee who is also grantor folded into one row. */
export const toRelatedPeople = (entityGUID: string, links: PersonOrgLink[]): RelatedPerson[] => {
    const byMember = new Map<string, PersonOrgLink[]>();
    links
        .filter((link) => link.parentPersonOrgGUID === entityGUID && ENTITY_ROLE_TYPES.has(link.membershipType))
        .forEach((link) =>
            byMember.set(link.memberPersonOrgGUID, [...(byMember.get(link.memberPersonOrgGUID) ?? []), link]),
        );
    return [...byMember].map(([personOrgGUID, memberLinks]) => {
        const types = new Set(memberLinks.map((link) => link.membershipType));
        const role =
            [...ENTITY_ROLES]
                .sort((a, b) => b.types.length - a.types.length)
                .find((candidate) => candidate.types.every((type) => types.has(type)))?.value ?? '';
        const percent = memberLinks.find((link) => PERCENT_TYPES.has(link.membershipType))?.membershipPercent;
        return { personOrgGUID, role, percent: percent === undefined ? '' : String(Number(percent)) };
    });
};

/** Percents add up in hundredths, so 10.1 + 20.2 + 69.7 is 100 and not 99.99999999999999. */
export const isHundred = (total: number) => Math.round(total * 100) === 10000;

export const toHundredths = (value: string) => Math.round((Number(value) || 0) * 100);

/** Each percent-carrying type's total, keyed by type; a role is complete when its total is 100. */
export const percentTotals = (rows: RelatedPerson[]) => {
    const totals = new Map<MembershipType, number>();
    rows.forEach((row) => {
        const type = percentTypeOf(row.role);
        if (type) totals.set(type, (totals.get(type) ?? 0) + toHundredths(row.percent));
    });
    return [...totals].map(([type, hundredths]) => ({ type, total: hundredths / 100 }));
};

export const arePercentsComplete = (rows: RelatedPerson[]) =>
    percentTotals(rows).every(({ total }) => isHundred(total));

/** The rows with each percent-carrying role split evenly, the rounding remainder going to the first. */
export const splitPercentsEvenly = (rows: RelatedPerson[]): RelatedPerson[] => {
    const groups = new Map<MembershipType, number[]>();
    rows.forEach((row, index) => {
        const type = percentTypeOf(row.role);
        if (type) groups.set(type, [...(groups.get(type) ?? []), index]);
    });
    const percents = new Map<number, string>();
    groups.forEach((indexes) => {
        const share = Math.floor(10000 / indexes.length);
        indexes.forEach((index, position) =>
            percents.set(index, String((share + (position === 0 ? 10000 - share * indexes.length : 0)) / 100)),
        );
    });
    return rows.map((row, index) => ({ ...row, percent: percents.get(index) ?? '' }));
};

/**
 * The entity's whole link set for the onboarding save, which replaces it in one transaction: the edited rows, each
 * role as its links (a percent only where the type carries one, the stored authorization flags kept), plus the stored
 * links of types this editor does not show, such as trust beneficiaries.
 */
export const toEntityLinks = (
    entityGUID: string,
    rows: RelatedPerson[],
    storedLinks: PersonOrgLink[],
): PersonOrgLinkSaveRequest[] => {
    const stored = storedLinks.filter((link) => link.parentPersonOrgGUID === entityGUID);
    const toRequest = (link: PersonOrgLink): PersonOrgLinkSaveRequest => ({
        memberPersonOrgGUID: link.memberPersonOrgGUID,
        membershipType: link.membershipType,
        membershipPercent: link.membershipPercent,
        authorizedToExecuteDocumentsAndLegalAgreements: link.authorizedToExecuteDocumentsAndLegalAgreements,
        authorizedForPortfolioManagement: link.authorizedForPortfolioManagement,
        authorizedForWithdrawals: link.authorizedForWithdrawals,
    });
    const edited = rows
        .filter((row) => row.personOrgGUID)
        .flatMap((row) =>
            membershipTypesOf(row.role).map((membershipType) => {
                const before = stored.find(
                    (link) => link.memberPersonOrgGUID === row.personOrgGUID && link.membershipType === membershipType,
                );
                return {
                    ...(before ? toRequest(before) : { memberPersonOrgGUID: row.personOrgGUID, membershipType }),
                    membershipPercent: PERCENT_TYPES.has(membershipType) ? row.percent || '0' : undefined,
                };
            }),
        );
    return [...stored.filter((link) => !ENTITY_ROLE_TYPES.has(link.membershipType)).map(toRequest), ...edited];
};

export const isEntity = (personOrg: PersonOrg) =>
    personOrg.personOrgType === PersonOrgType.TRUST ||
    personOrg.personOrgType === PersonOrgType.ORGANIZATION ||
    personOrg.personOrgType === PersonOrgType.FOUNDATION;

export const isJoint = (personOrg?: PersonOrg) => personOrg?.personOrgType === PersonOrgType.JOINT_PERSONS;

export const householdRole = (profile: PersonOrgProfile, globalAccountGUID: string) =>
    profile.globalAccountPersonOrgRoles.find((role) => role.globalAccountGUID === globalAccountGUID);

/** Any label but a client's or family's marks a professional: a listed role or a custom one. */
const isProfessionalLabel = (roleLabel?: string) =>
    !!roleLabel && roleLabel !== CLIENT_LABEL && roleLabel !== FAMILY_LABEL;

/** A professional's role; `undefined` for anyone else. */
export const professionalRole = (profile: PersonOrgProfile, globalAccountGUID: string) => {
    const label = householdRole(profile, globalAccountGUID)?.roleLabel;
    return isProfessionalLabel(label) ? label : undefined;
};

export const kindOf = (
    profile: PersonOrgProfile,
    globalAccountGUID: string,
    accountOwnerGUIDs: Set<string>,
): PersonKind | undefined => {
    const { personOrg } = profile;
    if (isJoint(personOrg)) return undefined;
    if (isEntity(personOrg)) return PersonKind.ENTITY;
    const role = householdRole(profile, globalAccountGUID);
    const owner = accountOwnerGUIDs.has(profile.personOrgGUID);
    if (role?.roleType === GlobalAccountRoleType.SECONDARY_USER && !owner && isProfessionalLabel(role.roleLabel)) {
        return PersonKind.PROFESSIONAL;
    }
    if (role?.roleLabel === FAMILY_LABEL && !owner) return PersonKind.FAMILY;
    if (role?.roleLabel === CLIENT_LABEL || role?.roleType === GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER || owner) {
        return PersonKind.CLIENT;
    }
    return PersonKind.FAMILY;
};

// ---------------------------------------------------------------------------------------------------------------
// Family relationships, derived from `PersonOrgRelation` towards the household's primary client
// ---------------------------------------------------------------------------------------------------------------

export const RELATION_LABELS: Record<PersonOrgRelationType, string> = {
    [PersonOrgRelationType.SPOUSE]: 'Spouse',
    [PersonOrgRelationType.CHILD]: 'Child',
    [PersonOrgRelationType.PARENT]: 'Parent',
    [PersonOrgRelationType.SIBLING]: 'Sibling',
    [PersonOrgRelationType.GRAND_CHILD]: 'Grandchild',
    [PersonOrgRelationType.GRAND_PARENT]: 'Grandparent',
    [PersonOrgRelationType.RELATIVE]: 'Relative',
    [PersonOrgRelationType.OTHER]: 'Other',
};

const REVERSE_RELATION: Record<PersonOrgRelationType, PersonOrgRelationType> = {
    [PersonOrgRelationType.SPOUSE]: PersonOrgRelationType.SPOUSE,
    [PersonOrgRelationType.CHILD]: PersonOrgRelationType.PARENT,
    [PersonOrgRelationType.PARENT]: PersonOrgRelationType.CHILD,
    [PersonOrgRelationType.SIBLING]: PersonOrgRelationType.SIBLING,
    [PersonOrgRelationType.GRAND_CHILD]: PersonOrgRelationType.GRAND_PARENT,
    [PersonOrgRelationType.GRAND_PARENT]: PersonOrgRelationType.GRAND_CHILD,
    [PersonOrgRelationType.RELATIVE]: PersonOrgRelationType.RELATIVE,
    [PersonOrgRelationType.OTHER]: PersonOrgRelationType.OTHER,
};

/** What `relatedGUID` is to `anchorGUID` ("Child"), from relations stored in either direction. */
export const relationTo = (relations: PersonOrgRelation[], anchorGUID: string, relatedGUID: string) => {
    const direct = relations.find((r) => r.personOrgGUID === anchorGUID && r.relatedPersonOrgGUID === relatedGUID);
    if (direct) return direct.relationType;
    const reverse = relations.find((r) => r.personOrgGUID === relatedGUID && r.relatedPersonOrgGUID === anchorGUID);
    return reverse ? REVERSE_RELATION[reverse.relationType] : undefined;
};

export const toRelation = (
    anchorGUID: string,
    relatedGUID: string,
    relationType: PersonOrgRelationType,
): PersonOrgRelation => ({
    personOrgGUID: anchorGUID,
    relatedPersonOrgGUID: relatedGUID,
    relationType,
    reverseRelationType: REVERSE_RELATION[relationType],
});

// ---------------------------------------------------------------------------------------------------------------
// The onboarding profile save
// ---------------------------------------------------------------------------------------------------------------

/** Every key of T, optional ones included. */
export type AllKeys<T> = { [K in keyof Required<T>]: T[K] };

/** The backend answers an empty trusted contact as `{ personOrgGUID: null }` but refuses it back, so it is none. */
export const trustedContactOrNone = (trustedContact?: TrustedContact) => {
    const { personOrgGUID, firstName, lastName, email, phone } = trustedContact ?? {};
    return personOrgGUID || firstName || lastName || email || phone ? trustedContact : undefined;
};

/** A trusted contact entered by name (in the portal) rather than picked from the household. */
export const STANDALONE_CONTACT = 'STANDALONE';
/** The client declined to name a trusted contact; none picked means not asked yet. */
export const DECLINED_CONTACT = 'DECLINED';

export const contactValue = (personOrg: PersonOrg) => {
    const contact = trustedContactOrNone(personOrg.trustedContact);
    if (!contact) return personOrg.trustedContactDeclined ? DECLINED_CONTACT : '';
    return contact.personOrgGUID ?? STANDALONE_CONTACT;
};

export interface StandaloneContactValues {
    firstName: string;
    lastName: string;
    relationship: string;
    phone: string;
    email: string;
}

export const toStandaloneContactValues = (personOrg: PersonOrg): StandaloneContactValues => {
    const contact = contactValue(personOrg) === STANDALONE_CONTACT ? personOrg.trustedContact : undefined;
    return {
        firstName: contact?.firstName ?? '',
        lastName: contact?.lastName ?? '',
        relationship: contact?.relationship ?? '',
        phone: contact?.phone ?? '',
        email: contact?.email ?? '',
    };
};

export const toStandaloneContact = (contact: StandaloneContactValues): TrustedContact => ({
    firstName: contact.firstName.trim(),
    lastName: contact.lastName.trim(),
    relationship: contact.relationship.trim() || undefined,
    phone: contact.phone.trim(),
    email: contact.email.trim(),
});

/** The trusted contact to save; an unchanged household pick resends the stored one whole, with its relationship. */
export const toContactChanges = (personOrg: PersonOrg, picked: string, standalone: StandaloneContactValues) => {
    if (picked === STANDALONE_CONTACT) {
        return { trustedContact: toStandaloneContact(standalone), trustedContactDeclined: undefined };
    }
    if (picked === contactValue(personOrg)) {
        return {
            trustedContact: trustedContactOrNone(personOrg.trustedContact),
            trustedContactDeclined: personOrg.trustedContactDeclined,
        };
    }
    return {
        trustedContact: picked && picked !== DECLINED_CONTACT ? { personOrgGUID: picked } : undefined,
        trustedContactDeclined: picked === DECLINED_CONTACT || undefined,
    };
};

/**
 * The person's current values as an onboarding update; the save replaces every field, so a change is sent on top of
 * this. `satisfies AllKeys` turns a field added to the request into a type error here instead of a silent wipe.
 */
export const toOnboardingUpdateRequest = (personOrg: PersonOrg): PersonOrgOnboardingUpdateRequest =>
    ({
        personOrgSubtype: personOrg.personOrgSubtype,
        archived: personOrg.archived,
        firstName: personOrg.firstName,
        middleName: personOrg.middleName,
        lastName: personOrg.lastName,
        title: personOrg.title,
        maritalStatus: personOrg.maritalStatus,
        publicCompanyControlPerson: personOrg.publicCompanyControlPerson,
        publicCompanyName: personOrg.publicCompanyName,
        publicCompanyTicker: personOrg.publicCompanyTicker,
        accreditedInvestor: personOrg.accreditedInvestor,
        trustedContact: trustedContactOrNone(personOrg.trustedContact),
        trustedContactDeclined: personOrg.trustedContactDeclined,
        taxFilingStatus: personOrg.taxFilingStatus,
        dateOfBirth: personOrg.dateOfBirth,
        dateOfDeath: personOrg.dateOfDeath,
        numberOfDependents: personOrg.numberOfDependents,
        primaryEmail: personOrg.primaryEmail,
        personalPhone: personOrg.personalPhone,
        orgName: personOrg.orgName,
        orgEstablishedAt: personOrg.orgEstablishedAt,
        orgState: personOrg.orgState,
        orgCountry: personOrg.orgCountry,
        employmentStatus: personOrg.employmentStatus as EmploymentStatus[] | undefined,
        employerName: personOrg.employerName,
        occupation: personOrg.occupation,
        employerPhone: personOrg.employerPhone,
        employerAffiliatedWithFinancialFirm: personOrg.employerAffiliatedWithFinancialFirm,
        employerComplianceOfficerEmail: personOrg.employerComplianceOfficerEmail,
        wealthSources: personOrg.wealthSources as WealthSource[] | undefined,
        wealthSourcesOther: personOrg.wealthSourcesOther,
        requiredToBeRegisteredDerivativeTrader: personOrg.requiredToBeRegisteredDerivativeTrader,
        subjectOfFinancialDispute: personOrg.subjectOfFinancialDispute,
        subjectOfFinancialDisputeDetails: personOrg.subjectOfFinancialDisputeDetails,
        subjectOfExchangeInvestigation: personOrg.subjectOfExchangeInvestigation,
        subjectOfExchangeInvestigationDetails: personOrg.subjectOfExchangeInvestigationDetails,
        familyAffiliatedWithFinancialFirm: personOrg.familyAffiliatedWithFinancialFirm,
        familyAffiliatedWithFinancialFirmDetails: personOrg.familyAffiliatedWithFinancialFirmDetails,
        affiliatedFamilyMemberGUID: personOrg.affiliatedFamilyMemberGUID,
        registeredWithNationalFuturesAssociation: personOrg.registeredWithNationalFuturesAssociation,
        countryOfBirth: personOrg.countryOfBirth,
        currentUsCitizen: personOrg.currentUsCitizen,
        citizenshipCountries: personOrg.citizenshipCountries as CountryCode[] | undefined,
    }) satisfies AllKeys<PersonOrgOnboardingUpdateRequest>;

export const data = (personOrgGUID: string, dataType: DataType, value: string) =>
    encryptData(personOrgGUID, dataType, value.trim());

export interface AddressValues {
    street: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
}

export const toAddressValues = (address?: PersonOrgAddress): AddressValues => ({
    street: text(address?.street),
    street2: text(address?.street2),
    city: text(address?.city),
    state: address?.state ?? '',
    postalCode: text(address?.postalCode),
});

/** The address request; the card edits US addresses, so a stored country is kept rather than set to US. */
export const toAddressRequest = (
    personOrgGUID: string,
    addressType: AddressType,
    values: AddressValues,
    stored?: PersonOrgAddress,
): PersonOrgAddressCreateRequest => ({
    addressType,
    street: data(personOrgGUID, DataType.PERSON_ORG_ADDRESS_STREET, values.street),
    street2: data(personOrgGUID, DataType.PERSON_ORG_ADDRESS_STREET2, values.street2),
    city: data(personOrgGUID, DataType.PERSON_ORG_ADDRESS_CITY, values.city),
    state: values.state || undefined,
    postalCode: data(personOrgGUID, DataType.PERSON_ORG_ADDRESS_POSTAL_CODE, values.postalCode),
    countryCode: stored?.countryCode ?? CountryCode.US,
});

/**
 * The address to save: `undefined` (keep the stored one) while the card left it as stored, so an unrelated edit never
 * rewrites it; `null` (delete it) once every part is cleared; else the address as typed, an incomplete one too, which
 * the card's missing count flags.
 */
export const changedAddress = (
    personOrgGUID: string,
    addressType: AddressType,
    values: AddressValues,
    stored?: PersonOrgAddress,
) => {
    const typed = trimAddress(values);
    const kept = trimAddress(toAddressValues(stored));
    const parts = Object.keys(typed) as (keyof AddressValues)[];
    if (parts.every((part) => typed[part] === kept[part])) return undefined;
    return parts.some((part) => typed[part]) ? toAddressRequest(personOrgGUID, addressType, typed, stored) : null;
};

const trimAddress = (values: AddressValues): AddressValues => ({
    street: values.street.trim(),
    street2: values.street2.trim(),
    city: values.city.trim(),
    state: values.state.trim(),
    postalCode: values.postalCode.trim(),
});

const keepAddress = (address?: PersonOrgAddress): PersonOrgAddressCreateRequest | undefined =>
    address && {
        addressType: address.addressType,
        street: address.street,
        street2: address.street2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
    };

/**
 * The onboarding profile save for one card. The endpoint replaces the person's onboarding fields and deletes any
 * address, financial or compliance information left out, so everything the card does not edit is resent as stored.
 * `undefined` keeps an address, `null` deletes it.
 */
export const toProfileSaveRequest = (
    {
        personOrg,
        primaryAddress,
        mailingAddress,
        employerAddress,
        financialInformation,
        complianceInformation,
    }: PersonOrgProfile,
    changes: Partial<PersonOrgOnboardingUpdateRequest>,
    addresses: {
        primary?: PersonOrgAddressCreateRequest | null;
        mailing?: PersonOrgAddressCreateRequest | null;
        employer?: PersonOrgAddressCreateRequest | null;
    } = {},
    /** The whole link set of a joint or entity; left out, the stored links are kept. */
    personOrgLinks?: PersonOrgLinkSaveRequest[],
): PersonOrgOnboardingProfileSaveRequest => ({
    personOrg: { ...toOnboardingUpdateRequest(personOrg), ...changes },
    primaryAddress: addresses.primary === undefined ? keepAddress(primaryAddress) : (addresses.primary ?? undefined),
    mailingAddress: addresses.mailing === undefined ? keepAddress(mailingAddress) : (addresses.mailing ?? undefined),
    employerAddress:
        addresses.employer === undefined ? keepAddress(employerAddress) : (addresses.employer ?? undefined),
    financialInformation: financialInformation && {
        highNetWorth: financialInformation.highNetWorth,
        netWorth: financialInformation.netWorth,
        liquidNetWorth: financialInformation.liquidNetWorth,
        totalAssets: financialInformation.totalAssets,
        annualNetIncome: financialInformation.annualNetIncome,
    },
    complianceInformation: complianceInformation && {
        section13DNdvrAssetIdList: complianceInformation.section13DNdvrAssetIdList?.assets,
        section16NdvrAssetIdList: complianceInformation.section16NdvrAssetIdList?.assets,
    },
    personOrgLinks,
});

/** The four parts Fidelity needs, each counted as one field by a card's missing count. */
export const requiredAddressParts = (values: AddressValues) => [
    !!values.street.trim(),
    !!values.city.trim(),
    !!values.state,
    !!values.postalCode.trim(),
];

export const formatAddress = (values: AddressValues) =>
    [values.street, values.street2, values.city, [values.state, values.postalCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

/** Today as a date field's `max`, so a birth or founding date cannot lie ahead. */
export const todayInput = () => format(new Date(), 'yyyy-MM-dd');

// ---------------------------------------------------------------------------------------------------------------
// Shared display helpers
// ---------------------------------------------------------------------------------------------------------------

/** "a, b and c". */
export const joinNames = (names: string[]) =>
    names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** "Complete" or "3 missing", as the card headers show it. */
export const completion = (missing: number) => ({
    complete: missing === 0,
    label: missing === 0 ? 'Complete' : `${missing} missing`,
});

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
export const COUNTRY_OPTIONS = Object.values(CountryCode)
    .map((code) => ({ value: code, label: countryNames.of(code) ?? code }))
    .sort((a, b) => a.label.localeCompare(b.label));

// Keep aligned with commons-util-core's EmailValidator, used by the login API.
const MAX_EMAIL_LENGTH = 255;
const EMAIL_PATTERN = /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/;

export const isValidEmail = (email: string): boolean => email.length <= MAX_EMAIL_LENGTH && EMAIL_PATTERN.test(email);
